/**
 * Catalog table — renders the unified Tree / Flat view with the 9
 * spec'd columns and responsive tiers. Drag-drop, drift bucket, and
 * the right-pane wiring are owned by the parent ``CatalogWorkspace``;
 * this component renders the visual surface and delegates intents
 * back via callbacks so the workspace can drive URL state.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Space, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DragOutlined,
  FolderOpenOutlined,
  RightOutlined,
  DownOutlined,
} from "@ant-design/icons";

import type {
  CategoryRow,
  ChainRow,
  ProductGroupRow,
  StockArticleEntry,
} from "../api/client";
import {
  type ArticleDragItem,
  type CategoryDragItem,
  type DragItem,
  type GroupDragItem,
  DRAG_ARTICLE,
  DRAG_CATEGORY,
  DRAG_GROUP,
} from "./CatalogDnd";
import {
  MultiFilterDropdown,
  RangeFilterDropdown,
  TextFilterDropdown,
  ToggleFilterDropdown,
  bindFilter,
  useContainerVisibleColumns,
  useStableTableComponents,
} from "./workspace";

const { Text } = Typography;

/** Container-width thresholds (px) below which each column is hidden.
 *  Name + Mismatch are always shown; the rest are progressively dropped
 *  as the catalog container narrows so the table never needs a
 *  horizontal scrollbar. Tweak in concert with each column's ``width``. */
const COLUMN_MIN_CONTAINER_WIDTH: Record<string, number> = {
  // Mobile NEEDS the grip handle — it's the only touch-safe initiator
  // (the rest of the row uses ``touch-action: pan-y`` so taps scroll
  // instead of starting a drag). Never hide it.
  grip: 0,
  name: 0,
  mismatch: 0,
  // Unit + Stock are the most operationally useful "what is this / how
  // much do we have" pair after Name. We need ~520 px before both fit
  // alongside Name + Mismatch without squeezing names into 2 lines.
  unit: 520,
  expected: 580,
  // Revenue / Profit add the time-window earnings view.
  revenue: 700,
  profit: 800,
  // Sell / Buy / Margin only show on comfortably wide tables.
  sell: 920,
  buy: 1000,
  margin: 1080,
  // Stock-preset columns. Operator visibility is applied first (the
  // preset/picker); these thresholds only further trim when the
  // container is too narrow for the chosen set.
  refilled: 560,
  sold: 600,
  counted: 660,
  mismatch_pct: 760,
  doc: 840,
  last_refill: 700,
};

/** Below this catalog container width, hide the inline ``+ group`` /
 *  ``+ article`` action links inside category/group rows — they push
 *  the name into a 2nd line which is harder to scan. The toolbar's
 *  Add buttons cover the same intent and stay visible at all widths. */
const NARROW_HIDE_INLINE_ACTIONS = 540;

export type FlatRow =
  | {
      kind: "category";
      key: string;
      category: CategoryRow;
      collapsed: boolean;
      member_count: number;
      profit_cents: number | null;
      revenue_cents: number | null;
      expected: number;
      mismatch: number | null;
      /** True when any currently-filtered member article is low-stock. */
      isLow?: boolean;
    }
  | {
      kind: "group";
      key: string;
      group: ProductGroupRow;
      collapsed: boolean;
      member_count: number;
      profit_cents: number | null;
      revenue_cents: number | null;
      expected: number;
      mismatch: number | null;
      sharedUnit: string | null;
      /** True when any currently-filtered member article is low-stock. */
      isLow?: boolean;
    }
  | {
      kind: "article";
      key: string;
      article: ChainRow;
      profit_cents: number | null;
      revenue_cents: number | null;
      expected: number;
      mismatch: number | null;
      /** Windowed stock stats from ``/api/catalog/stock`` (refilled/sold/
       * counted/mismatch%/days-cover/last-refill + the since-delivery
       * baseline). ``undefined`` when the stock fetch wasn't needed. */
      stock?: StockArticleEntry | null;
      /** True when this article is below the low-stock highlight threshold. */
      isLow?: boolean;
      /** True when this article is categorised but not in any product
       * group — render at category-depth with a "direct child of
       * category" tree-line so it doesn't visually attach to the last
       * group above. */
      ungroupedInCategory?: boolean;
    }
  | {
      kind: "placeholder";
      key: string;
      product_group_id: number;
      category_id: number;
    }
  | {
      // Virtual header for the DELETED bucket. Visually mimics a
      // category row but is NOT a real category — backend never sees
      // it. Articles below have ``article.active === false``. Not a
      // drop target. Numeric fields are nulls so the existing summary
      // column renders fall back to their "no data" cells without
      // special-casing this variant.
      kind: "deleted-header";
      key: string;
      member_count: number;
      profit_cents: null;
      revenue_cents: null;
      expected: null;
      mismatch: null;
    }
  | {
      // Virtual header for the INACTIVE bucket — soft-deactivated
      // categories (+ their groups/articles) appended at the very
      // bottom so the operator can audit + reactivate. Same shape /
      // null-numerics rationale as deleted-header.
      kind: "inactive-header";
      key: string;
      member_count: number;
      profit_cents: null;
      revenue_cents: null;
      expected: null;
      mismatch: null;
    };

interface Props {
  rows: FlatRow[];
  /** True when an article is selected; the table compresses to Name + sorted column. */
  compressed: boolean;
  sortColumn: string | null;
  sortDir: "asc" | "desc";
  selectedArticleId: number | null;
  selectedCategoryId: number | null;
  selectedGroupId: number | null;
  onSelectArticle: (chainRootId: number) => void;
  onSelectCategory: (categoryId: number) => void;
  onSelectGroup: (groupId: number) => void;
  onSortChange: (column: string | null, dir: "asc" | "desc") => void;
  onToggleCollapse: (key: string) => void;
  /** Inline action affordances. */
  onAddArticleToGroup: (categoryId: number, groupId: number) => void;
  onAddGroupToCategory: (categoryId: number) => void;
  /** Drag-drop intents — workspace dispatches the API calls. */
  onReorderCategories: (orderedIds: number[]) => void;
  onReorderGroups: (categoryId: number, orderedGroupIds: number[]) => void;
  /**
   * Move a group to a different category. ``insertBeforeGroupId``
   * positions it before that specific group within the new category;
   * ``null`` appends to the end. The workspace dispatches a PATCH (for
   * category_id) followed by a reorder PUT (for sort_order).
   *
   * ``targetCategoryGroupOrder`` is the table's trusted snapshot of
   * the target category's existing group order (same snapshot used for
   * same-category reorders). The workspace splices the moved group
   * into this list instead of re-deriving from possibly-stale local
   * state — that derivation broke when the target category had no
   * groups in local state (e.g. mid-mutation) or when sort_order drift
   * had not yet flushed to the workspace's groups prop.
   */
  onMoveGroupToCategory: (
    groupId: number,
    targetCategoryId: number,
    insertBeforeGroupId: number | null,
    targetCategoryGroupOrder: number[],
  ) => void;
  /**
   * Reassign an article via drag-drop. When ``targetGroupId`` is
   * non-null, the backend derives the category from the group — pass
   * ``null`` for ``targetCategoryId`` to avoid reading a stale
   * ``category_id`` off the memoised flat row. When ``targetGroupId``
   * is null (drop on a category row = "Ungrouped"), pass the category
   * id. At least one of the two must be non-null.
   */
  onAssignArticle: (
    chainRootId: number,
    targetGroupId: number | null,
    targetCategoryId: number | null,
  ) => void;
  /** Disable drag/drop entirely (e.g. in Flat view where articles have
   *  no meaningful reorder — sort order determines position). */
  dragDisabled: boolean;
  /** When true (mobile), the drag source is attached ONLY to the grip
   *  cell so the rest of the row stays scrollable on touch. When false
   *  (desktop / mouse), the drag source is attached to the whole row
   *  so grabbing anywhere lifts it — there's no scroll-vs-drag
   *  conflict on mouse, and forcing operators to aim at a 32px handle
   *  on a wide screen is needlessly fiddly. */
  gripOnlyDrag: boolean;
  /** Pixel width of the catalog scroll container — drives which columns
   *  are visible. We can't use AntD's ``responsive`` prop because that
   *  reads the viewport, not our container; with the right pane open the
   *  viewport is wide but the catalog area might be narrow. */
  containerWidth: number;
  /** Operator-visible DATA columns (from the preset/column-picker), in
   *  any order. ``grip`` + ``name`` are always shown and not listed here.
   *  Applied before the container-width responsive trim. */
  visibleDataCols: string[];
  /** Per-column filter state + setter (URL-backed). */
  filters: Record<string, string>;
  onFilterChange: (column: string, raw: string) => void;
  /** Called when a collapsed row should auto-expand mid-drag. */
  onAutoExpand: (key: string) => void;
  /**
   * Getter for the catalog scroll container. AntD's sticky header
   * anchors to this element; we pass a thunk because the workspace's
   * ref isn't populated on the first render and we want the table to
   * read it lazily when AntD needs it. ``null`` falls back to window.
   */
  getScrollContainer: () => HTMLElement | null;
  /** chain_root_id → its recipe, for the "recipe" badge on recipe articles. */
  recipeRoots: ReadonlyMap<number, unknown>;
}

export const CatalogTable = ({
  rows,
  compressed,
  sortColumn,
  sortDir,
  selectedArticleId,
  selectedCategoryId,
  selectedGroupId,
  onSelectArticle,
  onSelectCategory,
  onSelectGroup,
  onSortChange,
  onToggleCollapse,
  onAddArticleToGroup,
  onAddGroupToCategory,
  onReorderCategories,
  onReorderGroups,
  onMoveGroupToCategory,
  onAssignArticle,
  dragDisabled,
  gripOnlyDrag,
  containerWidth,
  visibleDataCols,
  filters,
  onFilterChange,
  onAutoExpand,
  getScrollContainer,
  recipeRoots,
}: Props) => {
  // Snapshot the current ordering of categories + per-category groups so
  // drop handlers can compute the resulting order without re-querying the
  // backend. Rebuilds on every render — cheap (catalogs are small).
  const orderRefs = useMemo(() => {
    const catOrder: number[] = [];
    const groupOrderByCat: Record<number, number[]> = {};
    for (const r of rows) {
      if (r.kind === "category") catOrder.push(r.category.id);
      else if (r.kind === "group") {
        const list = groupOrderByCat[r.group.category_id] ?? [];
        list.push(r.group.id);
        groupOrderByCat[r.group.category_id] = list;
      }
    }
    return { catOrder, groupOrderByCat };
  }, [rows]);

  const hideInlineActions = containerWidth < NARROW_HIDE_INLINE_ACTIONS;
  const columns: ColumnsType<FlatRow> = useMemo(() => {
    if (compressed) {
      const showSortedExtra =
        sortColumn !== null && sortColumn !== "name";
      // Keep the grip column even in compressed mode so reorder /
      // article-drag still has a visible affordance while the right
      // pane is open. (Drag itself works on the whole row anyway,
      // but without the grip the operator can't tell.) Drop it in
      // dragDisabled mode (Flat) — the affordance would be a lie.
      const base: ColumnsType<FlatRow> = [];
      if (!dragDisabled) base.push(gripColumn());
      base.push(
        nameColumn(
          onToggleCollapse,
          onAddArticleToGroup,
          onAddGroupToCategory,
          filters,
          onFilterChange,
          hideInlineActions,
          sortColumn,
          sortDir,
          recipeRoots,
        ),
      );
      if (showSortedExtra) {
        base.push(sortedColumnFor(sortColumn));
      }
      return base;
    }
    return buildFullColumnSet(
      onToggleCollapse,
      onAddArticleToGroup,
      onAddGroupToCategory,
      sortColumn,
      sortDir,
      filters,
      onFilterChange,
      dragDisabled,
      hideInlineActions,
      recipeRoots,
    );
  }, [
    compressed,
    sortColumn,
    sortDir,
    hideInlineActions,
    onToggleCollapse,
    onAddArticleToGroup,
    onAddGroupToCategory,
    filters,
    onFilterChange,
    dragDisabled,
    recipeRoots,
  ]);

  // Operator visibility (preset / column-picker) is the FIRST filter:
  // keep grip + name (always on) plus the chosen data columns. The
  // container-width responsive trim then runs ON TOP — it only hides
  // further when cramped and never un-hides an operator-hidden column.
  // Skip in compressed mode (the Name + sorted-column set is already
  // hand-picked). ``visibleDataCols`` is joined for the dep so a new
  // array identity each render doesn't churn the memo.
  const visibleKey = visibleDataCols.join(",");
  const presetFilteredColumns = useMemo(() => {
    if (compressed) return columns;
    const allow = new Set(["grip", "name", ...visibleDataCols]);
    return columns.filter((c) =>
      allow.has(String((c as { key?: string }).key ?? "")),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, compressed, visibleKey]);

  // Drop columns the catalog container is too narrow to fit. The min-px
  // thresholds in COLUMN_MIN_CONTAINER_WIDTH were chosen empirically.
  // We deliberately don't use AntD's ``responsive: [...]`` because that
  // polls ``window.matchMedia`` at the viewport scale; when the right
  // pane is open the viewport is still wide but the catalog area
  // shrinks. The container-width measurement gives us truthful "what
  // actually fits" hiding without horizontal scroll.
  const visibleColumns = useContainerVisibleColumns(
    presetFilteredColumns,
    containerWidth,
    COLUMN_MIN_CONTAINER_WIDTH,
  );

  // Keep per-row lookup data accessible to the (stable) row component
  // via refs. The row component is memoised with [] deps so AntD sees a
  // stable component identity — without this, every parent re-render
  // would remount every <tr>, killing in-flight drag operations.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const handlersRef = useRef({
    orderRefs,
    onReorderCategories,
    onReorderGroups,
    onMoveGroupToCategory,
    onAssignArticle,
    onAutoExpand,
    dragDisabled,
    gripOnlyDrag,
  });
  handlersRef.current = {
    orderRefs,
    onReorderCategories,
    onReorderGroups,
    onMoveGroupToCategory,
    onAssignArticle,
    onAutoExpand,
    dragDisabled,
    gripOnlyDrag,
  };

  // ``useStableTableComponents`` memoises body.row with empty deps so
  // AntD reuses the same component identity across parent re-renders.
  // Fresh row data + handlers flow in via the mutable refs above.
  const tableComponents = useStableTableComponents<FlatRow, typeof handlersRef.current>({
    rowsRef,
    handlersRef,
    renderRow: (row, handlers, rowProps) => (
      <DraggableRow
        row={row}
        orderRefs={handlers.orderRefs}
        onReorderCategories={handlers.onReorderCategories}
        onReorderGroups={handlers.onReorderGroups}
        onMoveGroupToCategory={handlers.onMoveGroupToCategory}
        onAssignArticle={handlers.onAssignArticle}
        onAutoExpand={handlers.onAutoExpand}
        dragDisabled={handlers.dragDisabled}
        gripOnlyDrag={handlers.gripOnlyDrag}
        rowProps={rowProps}
      />
    ),
  });

  return (
    <Table<FlatRow>
      columns={visibleColumns}
      dataSource={rows}
      rowKey="key"
      pagination={false}
      sticky={{
        // ``getContainer`` is queried by AntD on every scroll event,
        // so we hand it our thunk directly — the workspace's scroll
        // ref hydrates after the first layout pass.
        offsetHeader: 0,
        getContainer: () => getScrollContainer() ?? window,
      }}
      size="middle"
      summary={(currentRows) =>
        compressed ? null : (
          <CatalogSummaryRow
            rows={currentRows as FlatRow[]}
            columns={visibleColumns}
          />
        )
      }
      components={tableComponents}
      onRow={(row) => ({
        onClick: (e) => {
          // Action affordances (chevron, grip, inline links) call
          // stopPropagation themselves; this fires for taps on the row
          // body / Name cell padding only.
          const target = e.target as HTMLElement;
          if (target.closest("a, button, .ant-select, input")) return;
          if (row.kind === "article") onSelectArticle(row.article.chain_root_id);
          else if (row.kind === "category") onSelectCategory(row.category.id);
          else if (row.kind === "group") onSelectGroup(row.group.id);
        },
        style:
          row.kind === "placeholder" ? undefined : { cursor: "pointer" },
      })}
      rowClassName={(row) => {
        const kind =
          row.kind === "category"
            ? "catalog-row-cat"
            : row.kind === "group"
              ? "catalog-row-grp"
              : row.kind === "article"
                ? "catalog-row-art"
                : "";
        const selected =
          (row.kind === "article" && row.article.chain_root_id === selectedArticleId) ||
          (row.kind === "category" && row.category.id === selectedCategoryId) ||
          (row.kind === "group" && row.group.id === selectedGroupId)
            ? " catalog-row-selected"
            : "";
        // Low-stock highlight (colour rule only) — ARTICLE rows only.
        // Category/group rows are intentionally NOT rolled up (operator
        // found whole-section red too heavy); the leaf rows carry the
        // signal.
        const low =
          row.kind === "article" && row.isLow ? " catalog-row-low" : "";
        return kind + selected + low;
      }}
      onChange={(_pag, _filters, sorter) => {
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        if (s && s.order) {
          onSortChange(
            String(s.columnKey ?? s.field),
            s.order === "ascend" ? "asc" : "desc",
          );
        } else {
          onSortChange(null, "desc");
        }
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Draggable row wrapper
// ---------------------------------------------------------------------------

interface OrderRefs {
  catOrder: number[];
  groupOrderByCat: Record<number, number[]>;
}

interface DraggableRowProps {
  row: FlatRow | undefined;
  orderRefs: OrderRefs;
  onReorderCategories: (orderedIds: number[]) => void;
  onReorderGroups: (categoryId: number, orderedGroupIds: number[]) => void;
  onMoveGroupToCategory: (
    groupId: number,
    targetCategoryId: number,
    insertBeforeGroupId: number | null,
    targetCategoryGroupOrder: number[],
  ) => void;
  onAssignArticle: (
    chainRootId: number,
    targetGroupId: number | null,
    targetCategoryId: number | null,
  ) => void;
  onAutoExpand: (key: string) => void;
  /** When true, the row is rendered without drag/drop wiring. */
  dragDisabled: boolean;
  /** See ``Props.gripOnlyDrag``. */
  gripOnlyDrag: boolean;
  rowProps: React.HTMLAttributes<HTMLTableRowElement> & {
    children?: React.ReactNode;
  };
}

function DraggableRow({
  row,
  orderRefs,
  onReorderCategories,
  onReorderGroups,
  onMoveGroupToCategory,
  onAssignArticle,
  onAutoExpand,
  dragDisabled,
  gripOnlyDrag,
  rowProps,
}: DraggableRowProps) {
  const ref = useRef<HTMLTableRowElement>(null);
  const hoverTimerRef = useRef<number | null>(null);

  // Stable container for everything the drag/drop specs need to read
  // at runtime. The specs are registered ONCE (empty deps) so react-dnd
  // doesn't re-register mid-drag — that was breaking reorder for every
  // row beyond the first because each parent re-render mutated the
  // spec's [row] dep and tore down the active drag.
  const liveRef = useRef({
    row,
    orderRefs,
    onReorderCategories,
    onReorderGroups,
    onMoveGroupToCategory,
    onAssignArticle,
    dragDisabled,
  });
  liveRef.current = {
    row,
    orderRefs,
    onReorderCategories,
    onReorderGroups,
    onMoveGroupToCategory,
    onAssignArticle,
    dragDisabled,
  };

  // Drag type derives directly from the live row. AntD reconciles
  // rows by ``key`` (which embeds the kind), so within a single fiber
  // ``row.kind`` is stable — but we deliberately don't cache it via
  // ``useRef`` because if the first render ever sees ``row=undefined``
  // (data flush mid-render), the cached value would lock to "noop" and
  // that fiber's drag would silently stay broken forever.
  const dragType =
    row?.kind === "category"
      ? DRAG_CATEGORY
      : row?.kind === "group"
        ? DRAG_GROUP
        : row?.kind === "article"
          ? DRAG_ARTICLE
          : "noop";

  const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>(
    () => ({
      type: dragType,
      canDrag: () => {
        if (liveRef.current.dragDisabled) return false;
        const r = liveRef.current.row;
        return r?.kind === "category" || r?.kind === "group" || r?.kind === "article";
      },
      item: () => {
        const r = liveRef.current.row;
        if (!r) throw new Error("DraggableRow used without row");
        if (r.kind === "category")
          return { kind: DRAG_CATEGORY, id: r.category.id };
        if (r.kind === "group")
          return {
            kind: DRAG_GROUP,
            id: r.group.id,
            categoryId: r.group.category_id,
          };
        if (r.kind === "article")
          return {
            kind: DRAG_ARTICLE,
            chainRootId: r.article.chain_root_id,
            fromGroupId: r.article.product_group_id,
            fromCategoryId: r.article.category_id,
          };
        return { kind: DRAG_ARTICLE, chainRootId: 0, fromGroupId: null, fromCategoryId: null };
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    // STABLE: dragType is captured at mount; everything else lives in
    // liveRef and is read each call.
    [dragType],
  );

  // Drop position within the hovered row — "before" or "after" the
  // target — based on the cursor's vertical position relative to the
  // row's midpoint. Backed by both a ref (read inside the drop
  // handler) AND React state (re-renders the row when the half flips,
  // so the insertion line moves live with the cursor). Without the
  // state, the visual indicator only updated on enter/leave because
  // react-dnd doesn't refresh ``collect`` on plain pointer moves.
  const dropPosRef = useRef<"before" | "after">("before");
  const [dropPos, setDropPos] = useState<"before" | "after">("before");

  const [{ isOver, canDrop, draggedKind }, drop] = useDrop<
    DragItem,
    void,
    {
      isOver: boolean;
      canDrop: boolean;
      draggedKind: string | null;
    }
  >(
    () => ({
      accept: [DRAG_CATEGORY, DRAG_GROUP, DRAG_ARTICLE],
      canDrop: (item) => {
        if (liveRef.current.dragDisabled) return false;
        return canDropOn(liveRef.current.row, item);
      },
      hover: (_item, monitor) => {
        // Fires on every pointer move while the cursor is over this
        // drop target. We use it to compute "drop on top half →
        // insert before / bottom half → insert after" and push that
        // through React state so the visual indicator flips live.
        if (!ref.current) return;
        const offset = monitor.getClientOffset();
        if (!offset) return;
        const rect = ref.current.getBoundingClientRect();
        const pos: "before" | "after" =
          offset.y < rect.top + rect.height / 2 ? "before" : "after";
        if (pos !== dropPosRef.current) {
          dropPosRef.current = pos;
          setDropPos(pos);
        }
      },
      drop: (item) => {
        const s = liveRef.current;
        const r = s.row;
        if (!r) return;
        const pos = dropPosRef.current;
        if (item.kind === DRAG_CATEGORY && r.kind === "category") {
          const dragged = (item as CategoryDragItem).id;
          if (dragged === r.category.id) return;
          const next = s.orderRefs.catOrder.filter((id) => id !== dragged);
          const targetIdx = next.indexOf(r.category.id);
          // ``before`` inserts at the target index; ``after`` inserts
          // at target + 1 so the dragged item lands AFTER the target.
          next.splice(pos === "before" ? targetIdx : targetIdx + 1, 0, dragged);
          s.onReorderCategories(next);
          return;
        }
        if (item.kind === DRAG_GROUP) {
          const draggedGroup = item as GroupDragItem;
          if (r.kind === "group") {
            if (draggedGroup.id === r.group.id) return;
            if (draggedGroup.categoryId === r.group.category_id) {
              // Same-category reorder: insert before/after the target.
              const list =
                s.orderRefs.groupOrderByCat[r.group.category_id] ?? [];
              const next = list.filter((id) => id !== draggedGroup.id);
              const targetIdx = next.indexOf(r.group.id);
              next.splice(
                pos === "before" ? targetIdx : targetIdx + 1,
                0,
                draggedGroup.id,
              );
              s.onReorderGroups(r.group.category_id, next);
              return;
            }
            // Cross-category: move the group (and its articles) to the
            // target's category, inserted before the target group in
            // the new category's order. Pass the table's trusted
            // snapshot of the target category's existing group order
            // so the workspace doesn't have to reconstruct it from
            // possibly-stale local state.
            s.onMoveGroupToCategory(
              draggedGroup.id,
              r.group.category_id,
              r.group.id,
              s.orderRefs.groupOrderByCat[r.group.category_id] ?? [],
            );
            return;
          }
          if (r.kind === "category") {
            if (r.category.id === draggedGroup.categoryId) return;
            // Drop on a different category row: move + append to end.
            // The target category may have no groups (empty array).
            s.onMoveGroupToCategory(
              draggedGroup.id,
              r.category.id,
              null,
              s.orderRefs.groupOrderByCat[r.category.id] ?? [],
            );
            return;
          }
          return;
        }
        if (item.kind === DRAG_ARTICLE) {
          const a = item as ArticleDragItem;
          if (r.kind === "group") {
            // Backend derives category from group; passing the
            // memoised flat row's ``category_id`` would race the
            // group's authoritative ``category_id`` if the group has
            // moved since this row snapshot.
            s.onAssignArticle(a.chainRootId, r.group.id, null);
            return;
          }
          if (r.kind === "placeholder") {
            // Same: server resolves category from product_group_id.
            s.onAssignArticle(a.chainRootId, r.product_group_id, null);
            return;
          }
          if (r.kind === "category") {
            // Drop onto the category row body = "Ungrouped in this
            // category". Visual: bg tint highlights the whole row so
            // the target is unambiguous. Here category_id is the
            // primary target (no group), so it must be non-null.
            s.onAssignArticle(a.chainRootId, null, r.category.id);
            return;
          }
        }
      },
      collect: (monitor) => {
        const item = monitor.getItem() as DragItem | null;
        return {
          isOver: monitor.isOver({ shallow: true }),
          canDrop: monitor.canDrop(),
          draggedKind: item?.kind ?? null,
        };
      },
    }),
    // STABLE: everything is read via liveRef.current.
    [],
  );

  // Drag-over auto-expand: hovering a collapsed category/group row for
  // ≥400ms expands it so the operator can drop into a previously-hidden
  // descendant. Cancels if the drag leaves before the timer fires.
  useEffect(() => {
    const shouldArm =
      isOver &&
      canDrop &&
      row !== undefined &&
      (row.kind === "category" || row.kind === "group") &&
      row.collapsed;
    if (!shouldArm) {
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
      return;
    }
    hoverTimerRef.current = window.setTimeout(() => {
      if (!row) return;
      onAutoExpand(row.key);
      hoverTimerRef.current = null;
    }, 400);
    return () => {
      if (hoverTimerRef.current !== null) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    };
  }, [isOver, canDrop, row, onAutoExpand]);

  // Mobile drag-handle wiring: the drop target stays on the whole
  // <tr> (operators can drop onto any part of the row), but the drag
  // SOURCE is the grip cell only — otherwise touch users can't scroll
  // because every finger-down on a row would initiate a drag. After
  // each render, we look up the grip handle inside the row via its
  // data attribute. If the DOM node differs from the previously
  // connected one, we hand the new node to react-dnd's ``drag``
  // connector. We DON'T detach on cleanup with ``drag(null)`` because
  // that would race an in-flight drag every parent re-render. The
  // <tr> itself gets ``touch-action: pan-y`` (in the style below) so
  // vertical scrolling wins over any incidental drag gesture on the
  // row body.
  drop(ref);
  // Drag-source connection. On desktop (mouse) we wire the WHOLE row
  // so grabbing anywhere lifts it — there's no scroll-vs-drag conflict
  // on pointer. On mobile (touch) we wire ONLY the grip cell so the
  // rest of the row stays scrollable via touch-action: pan-y; without
  // that constraint, every finger-down on a row body promotes to a
  // drag and the catalog becomes impossible to scroll.
  const dragHandleRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (gripOnlyDrag) {
      const node =
        ref.current?.querySelector<HTMLElement>(
          "[data-catalog-drag-handle]",
        ) ?? null;
      if (node !== dragHandleRef.current) {
        dragHandleRef.current = node;
        drag(node);
      }
      return;
    }
    // Desktop: wire the row itself. Clear any prior grip wiring so a
    // resize (mobile → desktop) hands the connector back to the row.
    if (dragHandleRef.current !== ref.current) {
      dragHandleRef.current = ref.current;
      drag(ref.current);
    }
  });

  // Visual indicator: insertion line above the row for REORDER drags
  // (category-on-category, group-on-group); background tint for DROP-INTO
  // drags (article onto group/placeholder/category). The decision needs
  // the dragged item's kind, not just the target's — an article hovering
  // over a group row is a "drop into" gesture, not "insert before".
  const showInsertionLine =
    isOver &&
    canDrop &&
    row !== undefined &&
    ((draggedKind === DRAG_CATEGORY && row.kind === "category") ||
      (draggedKind === DRAG_GROUP && row.kind === "group"));
  const showBgTint =
    isOver &&
    canDrop &&
    row !== undefined &&
    (draggedKind === DRAG_ARTICLE ||
      // Group → category drop = "move group into this category"
      (draggedKind === DRAG_GROUP && row.kind === "category"));

  const style: React.CSSProperties = {
    ...(rowProps.style ?? {}),
    opacity: isDragging ? 0.4 : 1,
    // Let touch users scroll vertically past the row — the drag
    // initiator is the grip cell (touch-action:none), which overrides
    // this for finger-downs on the handle itself.
    touchAction: "pan-y",
    background: showBgTint
      ? "var(--ant-color-primary-bg, rgba(22,119,255,0.08))"
      : undefined,
    boxShadow: showInsertionLine
      ? dropPos === "after"
        ? "inset 0 -2px 0 0 var(--ant-color-primary, #1677ff)"
        : "inset 0 2px 0 0 var(--ant-color-primary, #1677ff)"
      : undefined,
  };
  // AntD passes the row cells as children — render them explicitly so
  // the table doesn't show blank rows. The drag/drop refs go on the
  // wrapping <tr>.
  const { children, ...rest } = rowProps;
  return (
    <tr {...rest} ref={ref} style={style}>
      {children}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Totals footer — sticky last row with filter-aware sums.
// Only article rows contribute (category/group/placeholder rows would
// double-count via their roll-up cells). Per-unit columns (Sell, Buy,
// Margin) show a weighted average over units sold in the window — that's
// the most useful per-row interpretation; for "what did we move and at
// what blended price" the sum is in Revenue / Profit.
// ---------------------------------------------------------------------------

function CatalogSummaryRow({
  rows,
  columns,
}: {
  rows: FlatRow[];
  columns: ColumnsType<FlatRow>;
}) {
  const articles = rows.filter((r): r is Extract<FlatRow, { kind: "article" }> =>
    r.kind === "article",
  );
  const articleCount = articles.length;
  const sumRevenue = articles.reduce((s, r) => s + (r.revenue_cents ?? 0), 0);
  const sumProfit = articles.reduce((s, r) => s + (r.profit_cents ?? 0), 0);
  const sumStock = articles.reduce((s, r) => s + r.expected, 0);
  const haveMismatch = articles.some((r) => r.mismatch !== null);
  const sumMismatch = haveMismatch
    ? articles.reduce((s, r) => s + (r.mismatch ?? 0), 0)
    : null;
  // Weighted average margin across sold units in the window. Falls back
  // to a simple arithmetic mean of article-level margin% when nothing
  // has sold yet — better than showing blank.
  const totalUnits = articles.reduce(
    (s, r) => s + (r.article.unit_size ? 1 : 1),
    0,
  );
  void totalUnits;
  const blendedMargin =
    sumRevenue > 0 ? Math.round((sumProfit / sumRevenue) * 100) : null;

  return (
    <Table.Summary fixed>
      <Table.Summary.Row style={{ background: "var(--ant-color-fill-alter)" }}>
        {columns.map((col, idx) => {
          const key = (col as { key?: string }).key ?? `c${idx}`;
          const content = summaryCell(key, {
            articleCount,
            sumRevenue,
            sumProfit,
            sumStock,
            sumMismatch,
            blendedMargin,
          });
          return (
            <Table.Summary.Cell
              key={key}
              index={idx}
              align={(col as { align?: "left" | "right" | "center" }).align ?? "left"}
            >
              {content}
            </Table.Summary.Cell>
          );
        })}
      </Table.Summary.Row>
    </Table.Summary>
  );
}

interface SummaryStats {
  articleCount: number;
  sumRevenue: number;
  sumProfit: number;
  sumStock: number;
  sumMismatch: number | null;
  blendedMargin: number | null;
}

function summaryCell(key: string, s: SummaryStats): React.ReactNode {
  switch (key) {
    case "grip":
      return null;
    case "name":
      return (
        <Text strong>
          Σ {s.articleCount} article{s.articleCount === 1 ? "" : "s"}
        </Text>
      );
    case "unit":
      return null;
    case "sell":
    case "buy":
      // Per-unit prices don't sum meaningfully; leave blank rather than
      // showing a misleading "average" that ignores volume.
      return null;
    case "margin":
      return s.blendedMargin === null ? (
        <Text type="secondary">—</Text>
      ) : (
        <Text
          strong
          type={
            s.blendedMargin > 0
              ? "success"
              : s.blendedMargin < 0
                ? "danger"
                : undefined
          }
        >
          {s.blendedMargin}%
        </Text>
      );
    case "revenue":
      return <Text strong>{formatCents(s.sumRevenue)}</Text>;
    case "profit":
      return (
        <Text
          strong
          type={
            s.sumProfit > 0 ? "success" : s.sumProfit < 0 ? "danger" : undefined
          }
        >
          {formatCents(s.sumProfit)}
        </Text>
      );
    case "expected":
      return (
        <Text strong type={s.sumStock < 0 ? "danger" : undefined}>
          {s.sumStock}
        </Text>
      );
    case "mismatch":
      if (s.sumMismatch === null)
        return (
          <Text type="secondary" style={{ fontSize: 11 }}>
            partial
          </Text>
        );
      if (s.sumMismatch === 0) return <Tag color="green">+0</Tag>;
      if (s.sumMismatch < 0) return <Tag color="red">{s.sumMismatch}</Tag>;
      return <Tag color="gold">+{s.sumMismatch}</Tag>;
    default:
      return null;
  }
}

function canDropOn(row: FlatRow | undefined, item: DragItem): boolean {
  if (!row) return false;
  if (item.kind === DRAG_CATEGORY) {
    // Don't allow dropping a category onto itself.
    return row.kind === "category" && row.category.id !== item.id;
  }
  if (item.kind === DRAG_GROUP) {
    // A group can be reordered within its own category (drop on a
    // sibling group), moved to a different category (drop on any
    // group in another category — inserts before the target — or on
    // any category row — appends to end). Never drop onto itself.
    if (row.kind === "group") return row.group.id !== item.id;
    if (row.kind === "category") return row.category.id !== item.categoryId;
    return false;
  }
  // DRAG_ARTICLE
  if (row.kind === "group") return true;
  if (row.kind === "placeholder") return true;
  if (row.kind === "category") return true; // = Ungrouped in this category
  return false;
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

function gripColumn(): ColumnsType<FlatRow>[number] {
  return {
    title: "",
    key: "grip",
    width: 32,
    render: (_: unknown, row) =>
      row.kind === "placeholder" ? null : (
        <Tooltip title="Drag to reorder / reassign">
          {/*
           * Mobile-first drag affordance: a touch-action:none span that
           * captures pointer-down without the surrounding <tr>'s
           * pan-y. DraggableRow finds this span via the
           * ``data-catalog-drag-handle`` attribute and pipes the
           * react-dnd drag connector to it (drop stays on the <tr>).
           * The 44px tap-target satisfies WCAG/iOS minimums while
           * keeping the visible glyph centred.
           */}
          <span
            data-catalog-drag-handle="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 32,
              minHeight: 44,
              cursor: "grab",
              touchAction: "none",
              color: "var(--ant-color-text-tertiary)",
            }}
          >
            <DragOutlined />
          </span>
        </Tooltip>
      ),
  };
}

function nameColumn(
  onToggleCollapse: (key: string) => void,
  onAddArticle: (categoryId: number, groupId: number) => void,
  onAddGroup: (categoryId: number) => void,
  filters: Record<string, string>,
  onFilterChange: (column: string, raw: string) => void,
  hideInlineActions: boolean,
  sort: string | null,
  sortDir: "asc" | "desc",
  recipeRoots: ReadonlyMap<number, unknown>,
): ColumnsType<FlatRow>[number] {
  // Mixing controlled-sort columns (with sortOrder) and uncontrolled ones
  // (without) breaks AntD's sort-state cycling — clicking Name appeared
  // to do nothing because the column never received the current order.
  // Pass the same sortOrder shape as the numeric columns.
  const order: "ascend" | "descend" | null =
    sort === "name"
      ? sortDir === "asc"
        ? "ascend"
        : "descend"
      : null;
  return {
    title: "Name",
    key: "name",
    sorter: true,
    sortOrder: order,
    filteredValue: filters.name ? [filters.name] : null,
    filterDropdown: bindFilter(
      filters.name,
      (raw) => onFilterChange("name", raw),
      () => onFilterChange("name", ""),
      TextFilterDropdown,
    ),
    render: (_: unknown, row) => {
      if (row.kind === "deleted-header" || row.kind === "inactive-header") {
        const label = row.kind === "deleted-header" ? "DELETED" : "INACTIVE";
        // Virtual section header. No chevron (always expanded), no
        // add-group affordance, no drop target — purely a visual
        // separator above the soft-deleted / deactivated rows.
        return (
          <Space size={6} wrap={false}>
            <Tag
              color="red"
              style={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: 0.4,
                padding: "2px 10px",
              }}
            >
              {label}
            </Tag>
            <Text type="secondary">({row.member_count})</Text>
          </Space>
        );
      }
      if (row.kind === "category") {
        const inactive = !row.category.active;
        return (
          <Space size={6} wrap={false}>
            <ChevronToggle
              collapsed={row.collapsed}
              onToggle={() => onToggleCollapse(row.key)}
            />
            <FolderOpenOutlined style={{ fontSize: 16 }} />
            {inactive && (
              <Tag color="red" style={{ fontWeight: 600, marginRight: 0 }}>
                INACTIVE
              </Tag>
            )}
            <Text
              strong
              delete={inactive}
              style={{ fontSize: 15, textTransform: "uppercase", letterSpacing: 0.4 }}
            >
              {row.category.name}
            </Text>
            <Text type="secondary">({row.member_count})</Text>
            {!hideInlineActions && !inactive && (
              <span style={{ marginLeft: 12 }}>
                <a
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddGroup(row.category.id);
                  }}
                >
                  + group
                </a>
              </span>
            )}
          </Space>
        );
      }
      if (row.kind === "group") {
        // Indent + tree-line connector. The left-border makes the "I am
        // a child of the category above" relationship obvious even on
        // tightly-spaced rows.
        const inactive = !row.group.active;
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginLeft: 16,
              paddingLeft: 12,
              borderLeft: "2px solid var(--ant-color-border, #d9d9d9)",
              flexWrap: "nowrap",
              minWidth: 0,
            }}
          >
            <ChevronToggle
              collapsed={row.collapsed}
              onToggle={() => onToggleCollapse(row.key)}
            />
            {inactive && (
              <Tag color="red" style={{ fontWeight: 600, marginRight: 0 }}>
                INACTIVE
              </Tag>
            )}
            <Text
              strong
              delete={inactive}
              style={{ fontSize: 14, whiteSpace: "nowrap" }}
            >
              {row.group.name}
            </Text>
            <Text type="secondary">({row.member_count})</Text>
            {row.group.unit_type_warning && <Tag color="orange">mixed</Tag>}
            {!hideInlineActions && !inactive && (
              <span style={{ marginLeft: 12 }}>
                <a
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddArticle(row.group.category_id, row.group.id);
                  }}
                >
                  + article
                </a>
              </span>
            )}
          </div>
        );
      }
      if (row.kind === "placeholder") {
        return (
          <div
            style={{
              marginLeft: 48,
              paddingLeft: 16,
              borderLeft: "2px dashed var(--ant-color-border, #d9d9d9)",
              fontStyle: "italic",
              color: "var(--ant-color-text-tertiary)",
            }}
          >
            Drop article here
          </div>
        );
      }
      // Article row. Two visual variants depending on whether the
      // article sits inside a group (deep indent + solid tree-line) or
      // is ungrouped-in-category (shallower indent + dashed tree-line
      // so it reads as a direct child of the category, not as another
      // group's leaf).
      // Soft-deleted chains (strichliste leaf.active === false) get a
      // prominent red DELETED badge BEFORE the name. Only visible when
      // the "Show deleted" toggle is on (otherwise the chain is filtered
      // out upstream); putting the badge here keeps the cue local to
      // the cell so it's still obvious if the row appears via deep-link.
      const deletedBadge = !row.article.active ? (
        <Tag color="red" style={{ marginRight: 4, fontWeight: 600 }}>
          DELETED
        </Tag>
      ) : null;
      const nameNode = (
        <Text delete={!row.article.active}>{row.article.name}</Text>
      );
      // A recipe IS an article (its chain root) that depletes ingredients at
      // settle; tag it so recipes are recognisable in the catalogue.
      const recipeBadge = recipeRoots.has(row.article.chain_root_id) ? (
        <Tag color="purple" style={{ marginLeft: 4 }}>
          recipe
        </Tag>
      ) : null;
      if (row.ungroupedInCategory) {
        return (
          <div
            style={{
              marginLeft: 16,
              paddingLeft: 12,
              borderLeft: "2px dashed var(--ant-color-border, #d9d9d9)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {deletedBadge}
            {nameNode}
            {recipeBadge}
            {row.article.active && (
              <Text
                type="secondary"
                style={{ fontSize: 11, fontStyle: "italic" }}
              >
                ungrouped
              </Text>
            )}
          </div>
        );
      }
      return (
        <div
          style={{
            marginLeft: 48,
            paddingLeft: 16,
            borderLeft: "2px solid var(--ant-color-border, #d9d9d9)",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {deletedBadge}
          {nameNode}
          {recipeBadge}
        </div>
      );
    },
  };
}

function buildFullColumnSet(
  onToggleCollapse: (key: string) => void,
  onAddArticle: (categoryId: number, groupId: number) => void,
  onAddGroup: (categoryId: number) => void,
  sort: string | null,
  sortDir: "asc" | "desc",
  filters: Record<string, string>,
  onFilterChange: (column: string, raw: string) => void,
  dragDisabled: boolean,
  hideInlineActions: boolean,
  recipeRoots: ReadonlyMap<number, unknown>,
): ColumnsType<FlatRow> {
  const order = sort
    ? sortDir === "asc"
      ? ("ascend" as const)
      : ("descend" as const)
    : null;

  const sortOrder = (key: string) => (sort === key ? order : null);
  const filterChrome = (key: string) => ({
    filteredValue: filters[key] ? [filters[key]] : null,
  });
  const rangeFilter = (key: string, unit?: string) => ({
    ...filterChrome(key),
    filterDropdown: bindFilter(
      filters[key],
      (raw) => onFilterChange(key, raw),
      () => onFilterChange(key, ""),
      (p) => <RangeFilterDropdown {...p} unit={unit} />,
    ),
  });
  // Wrap header text so it never breaks across lines — AntD's narrow
  // currency columns + a filter icon + a sort indicator leave little
  // room and "Margin" / "Profit" used to wrap onto two rows.
  const nb = (label: React.ReactNode) => (
    <span style={{ whiteSpace: "nowrap" }}>{label}</span>
  );

  return [
    ...(dragDisabled ? [] : [gripColumn()]),
    nameColumn(
      onToggleCollapse,
      onAddArticle,
      onAddGroup,
      filters,
      onFilterChange,
      hideInlineActions,
      sort,
      sortDir,
      recipeRoots,
    ),
    {
      title: nb("Unit"),
      key: "unit",
      width: 80,
      sorter: true,
      sortOrder: sortOrder("unit"),
      ...filterChrome("unit"),
      filterDropdown: bindFilter(
        filters.unit,
        (raw) => onFilterChange("unit", raw),
        () => onFilterChange("unit", ""),
        (p) => (
          <MultiFilterDropdown
            {...p}
            options={[
              { value: "liters", label: "Liters" },
              { value: "grams", label: "Grams" },
              { value: "count", label: "Count" },
            ]}
          />
        ),
      ),
      render: (_: unknown, row) => {
        if (row.kind !== "article") {
          if (row.kind === "group" && row.sharedUnit) return row.sharedUnit;
          return null;
        }
        const a = row.article;
        const size = a.unit_size_override ?? a.unit_size;
        const type = a.unit_type_override ?? a.unit_type;
        if (!size || !type) return "—";
        const num = Number(size);
        const base =
          type === "liters"
            ? `${num} L`
            : type === "grams"
              ? `${num} g`
              : type === "count"
                ? `${num}×`
                : `${num} ${type}`;
        if (a.pack_size && a.pack_size > 1) {
          return (
            <Tooltip title={`Refill pack: ${a.pack_size} × ${base}`}>
              <span>
                {base}
                <Text type="secondary" style={{ marginLeft: 4, fontSize: 11 }}>
                  /{a.pack_size}p
                </Text>
              </span>
            </Tooltip>
          );
        }
        return base;
      },
    },
    {
      title: nb(<Tooltip title="Sell price (EUR)">Sell</Tooltip>),
      key: "sell",
      align: "right",
      width: 80,
      sorter: true,
      sortOrder: sortOrder("sell"),
      ...rangeFilter("sell", "EUR"),
      render: (_: unknown, row) =>
        row.kind === "article" ? formatCents(row.article.sell_price_cents) : null,
    },
    {
      title: nb(<Tooltip title="Buy price (EUR)">Buy</Tooltip>),
      key: "buy",
      align: "right",
      width: 80,
      sorter: true,
      sortOrder: sortOrder("buy"),
      ...rangeFilter("buy", "EUR"),
      render: (_: unknown, row) =>
        row.kind === "article" ? formatCents(row.article.buy_price_cents) : null,
    },
    {
      title: nb(<Tooltip title="Margin (sell − buy) / sell, %">Margin</Tooltip>),
      key: "margin",
      align: "right",
      width: 85,
      sorter: true,
      sortOrder: sortOrder("margin"),
      ...rangeFilter("margin", "%"),
      render: (_: unknown, row) => {
        if (row.kind !== "article") return null;
        const sell = row.article.sell_price_cents;
        const margin = row.article.margin_cents;
        if (margin === null || sell === 0) return "—";
        const pct = Math.round((margin / sell) * 100);
        return (
          <Text type={margin > 0 ? "success" : margin < 0 ? "danger" : undefined}>
            {pct}%
          </Text>
        );
      },
    },
    {
      title: nb(
        <Tooltip title="Revenue (sum of sales × sell-price-at-sale) in selected window, EUR">
          Revenue
        </Tooltip>,
      ),
      key: "revenue",
      align: "right",
      width: 90,
      sorter: true,
      sortOrder: sortOrder("revenue"),
      ...rangeFilter("revenue", "EUR"),
      render: (_: unknown, row) =>
        row.kind === "placeholder"
          ? null
          : formatCents(row.revenue_cents),
    },
    {
      title: nb(
        <Tooltip title="Profit (revenue − historical buy cost) in selected window, EUR">
          Profit
        </Tooltip>,
      ),
      key: "profit",
      align: "right",
      width: 90,
      sorter: true,
      sortOrder: sortOrder("profit"),
      ...rangeFilter("profit", "EUR"),
      render: (_: unknown, row) => {
        if (row.kind === "placeholder")
          return null;
        const p = row.profit_cents;
        if (p === null || p === undefined) return "—";
        return (
          <Text type={p > 0 ? "success" : p < 0 ? "danger" : undefined}>
            {formatCents(p)}
          </Text>
        );
      },
    },
    {
      title: nb(
        <Tooltip title="Current stock on hand: lifetime refills minus lifetime sales (+ last stocktake correction)">
          Stock
        </Tooltip>,
      ),
      key: "expected",
      align: "right",
      width: 75,
      sorter: true,
      sortOrder: sortOrder("expected"),
      ...rangeFilter("expected"),
      render: (_: unknown, row) =>
        row.kind === "placeholder" || row.kind === "deleted-header" || row.expected === null
          ? null
          : (
            <Text type={row.expected < 0 ? "danger" : undefined}>{row.expected}</Text>
          ),
    },
    {
      title: nb(<Tooltip title="Refilled in the selected window">Refilled</Tooltip>),
      key: "refilled",
      align: "right",
      width: 90,
      sorter: true,
      sortOrder: sortOrder("refilled"),
      render: (_: unknown, row) =>
        row.kind === "article" ? (row.stock?.refilled_qty ?? null) : null,
    },
    {
      title: nb(<Tooltip title="Sold in the selected window">Sold</Tooltip>),
      key: "sold",
      align: "right",
      width: 90,
      sorter: true,
      sortOrder: sortOrder("sold"),
      render: (_: unknown, row) =>
        row.kind === "article" ? (row.stock?.sold_qty ?? null) : null,
    },
    {
      title: nb(<Tooltip title="Counted quantity at the last stocktake">Counted</Tooltip>),
      key: "counted",
      align: "right",
      width: 90,
      sorter: true,
      sortOrder: sortOrder("counted"),
      render: (_: unknown, row) => {
        if (row.kind !== "article") return null;
        const c = row.stock?.counted_qty ?? row.article.counted_qty;
        if (c === null || c === undefined)
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              —
            </Text>
          );
        return c;
      },
    },
    {
      title: nb(
        <Tooltip title="The discrepancy found AT the last count: counted − the book's expected back then. Frozen — it does NOT drift as you sell or restock (that's the live Stock column). Positive = surplus / unlogged return, negative = shrinkage. Hover a value for the last-count date + drift since.">
          Mismatch
        </Tooltip>,
      ),
      key: "mismatch",
      align: "right",
      width: 95,
      sorter: true,
      sortOrder: sortOrder("mismatch"),
      ...filterChrome("mismatch"),
      filterDropdown: bindFilter(
        filters.mismatch,
        (raw) => onFilterChange("mismatch", raw),
        () => onFilterChange("mismatch", ""),
        (p) => (
          <ToggleFilterDropdown {...p} label="Any non-zero mismatch" />
        ),
      ),
      render: (_: unknown, row) => {
        if (row.kind === "placeholder")
          return null;
        const m = row.mismatch;
        if (m === null || m === undefined)
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              no count
            </Text>
          );
        const tag =
          m === 0 ? (
            <Tag color="green">+0</Tag>
          ) : m < 0 ? (
            <Tag color="red">{m}</Tag>
          ) : (
            <Tag color="gold">+{m}</Tag>
          );
        // Freshness: when it was last counted + how far the LIVE book has
        // drifted since (counted → now). Drift is a "recount to re-verify"
        // hint, NOT part of the discrepancy. Only article rows carry `stock`.
        const countedAt =
          row.kind === "article" ? (row.stock?.counted_at ?? null) : null;
        const counted =
          row.kind === "article"
            ? (row.stock?.counted_qty ?? row.article.counted_qty)
            : null;
        const moved =
          row.expected != null && counted != null
            ? row.expected - counted
            : null;
        if (!countedAt && (moved === null || moved === 0)) return tag;
        return (
          <Tooltip
            title={
              <span>
                Discrepancy found at the last count — frozen, it doesn’t move as
                you sell or restock.
                {countedAt ? ` Last counted ${formatDaysAgo(countedAt)}.` : ""}
                {moved !== null && moved !== 0
                  ? ` Book has moved ${moved > 0 ? "+" : ""}${moved} since` +
                    ` — recount to re-verify.`
                  : ""}
              </span>
            }
          >
            {tag}
          </Tooltip>
        );
      },
    },
    {
      title: nb(
        <Tooltip title="At-count discrepancy as % of the book at that count (mismatch / max(|expected at count|, 1))">
          Mismatch %
        </Tooltip>,
      ),
      key: "mismatch_pct",
      align: "right",
      width: 100,
      sorter: true,
      sortOrder: sortOrder("mismatch_pct"),
      render: (_: unknown, row) => {
        if (row.kind !== "article") return null;
        const pct = row.stock?.mismatch_pct;
        if (pct === null || pct === undefined)
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              —
            </Text>
          );
        const rounded = Math.round(pct * 100);
        return (
          <Text
            type={rounded < 0 ? "danger" : rounded > 0 ? "warning" : undefined}
          >
            {rounded > 0 ? `+${rounded}%` : `${rounded}%`}
          </Text>
        );
      },
    },
    {
      title: nb(
        <Tooltip title="Days of cover at the recent consumption rate">
          Days cover
        </Tooltip>,
      ),
      key: "doc",
      align: "right",
      width: 100,
      sorter: true,
      sortOrder: sortOrder("doc"),
      render: (_: unknown, row) => {
        if (row.kind !== "article") return null;
        const d = row.stock?.days_of_cover;
        if (d === null || d === undefined)
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              —
            </Text>
          );
        return <Text>{Math.round(d)}d</Text>;
      },
    },
    {
      title: nb(
        <Tooltip title="Last refill timestamp (lifetime, not window-bound)">
          Last refill
        </Tooltip>,
      ),
      key: "last_refill",
      width: 120,
      sorter: true,
      sortOrder: sortOrder("last_refill"),
      render: (_: unknown, row) => {
        if (row.kind !== "article") return null;
        const ts = row.stock?.last_refill_at;
        if (!ts)
          return (
            <Text type="secondary" style={{ fontSize: 11 }}>
              never
            </Text>
          );
        return <Text>{formatDaysAgo(ts)}</Text>;
      },
    },
  ];
}

/** "3d ago" / "2w ago" relative label for the Last-refill column. */
function formatDaysAgo(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return "in future";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 365)}y ago`;
}

function sortedColumnFor(key: string | null): ColumnsType<FlatRow>[number] {
  // Build a minimal "show me the value" column for compressed mode.
  if (key === "margin") {
    return {
      title: "Margin",
      key: "margin",
      align: "right",
      render: (_: unknown, row) =>
        row.kind === "article" ? formatMargin(row.article) : null,
    };
  }
  if (key === "sell") {
    return {
      title: "Sell",
      key: "sell",
      align: "right",
      render: (_: unknown, row) =>
        row.kind === "article" ? formatCents(row.article.sell_price_cents) : null,
    };
  }
  if (key === "buy") {
    return {
      title: "Buy",
      key: "buy",
      align: "right",
      render: (_: unknown, row) =>
        row.kind === "article" ? formatCents(row.article.buy_price_cents) : null,
    };
  }
  if (key === "profit") {
    return {
      title: "Profit",
      key: "profit",
      align: "right",
      render: (_: unknown, row) =>
        row.kind === "placeholder"
          ? null
          : formatCents(row.profit_cents),
    };
  }
  if (key === "expected") {
    return {
      title: "Stock",
      key: "expected",
      align: "right",
      render: (_: unknown, row) =>
        row.kind === "placeholder"
          ? null
          : row.expected,
    };
  }
  if (key === "mismatch") {
    return {
      title: "Mismatch",
      key: "mismatch",
      align: "right",
      render: (_: unknown, row) =>
        row.kind === "placeholder"
          ? null
          : row.mismatch,
    };
  }
  if (key === "unit") {
    return {
      title: "Unit",
      key: "unit",
      render: (_: unknown, row) => {
        if (row.kind !== "article") return null;
        const a = row.article;
        const size = a.unit_size_override ?? a.unit_size;
        const type = a.unit_type_override ?? a.unit_type;
        return size && type ? `${Number(size)} ${type}` : "—";
      },
    };
  }
  return { title: "", key: "blank", render: () => null };
}

function ChevronToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <a
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
    >
      {collapsed ? <RightOutlined /> : <DownOutlined />}
    </a>
  );
}

function formatCents(c: number | null | undefined): string {
  if (c === null || c === undefined) return "—";
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(c / 100);
}

function formatMargin(a: ChainRow): React.ReactNode {
  const sell = a.sell_price_cents;
  if (a.margin_cents === null || sell === 0) return "—";
  return Math.round((a.margin_cents / sell) * 100);
}
